<template>
  <div class="dashboard-block">
    <h2 v-if="bookingIsMine">My Details</h2>
    <h2 v-else>{{ !booking.is_offer ? $t('Client') : `Requestor` }} Details</h2>

    <div class="form-item__row" v-if="booking && booking.client">
      <div class="form-item__field" v-if="userCanSeeClientBasicDetails">
        <div class="input-group input-group-booking-detail">
          <span class="input-group-addon">Name</span>
          <p id="name" @copy="handleCopy" class="form-control">
            {{ booking.client.name }}

            <template v-if="$userIsAdmin" >
              -
              <router-link v-if="$userIsAdmin" tag="a" :to="{ name: 'clients.profile.show', params: { id: booking.client.id }}" >
                Profile
              </router-link>
              |
              <router-link v-if="$userIsAdmin" tag="a" :to="{ name: 'clients.requests.index', params: { clientId: booking.client.id}}" >
                Requests
              </router-link>
              |
              <router-link v-if="$userIsAdmin" tag="a" :to="{ name: 'clients.bookings.index', params: { clientId: booking.client.id }}" >
                Bookings
              </router-link>
            </template>

          </p>
        </div>
      </div>
      <div class="form-item__field">
        <div class="input-group input-group-booking-detail">
          <span class="input-group-addon">{{ $t('organisation') | sentenceCase }}</span>
          <p id="company" @copy="handleCopy" class="form-control">
            {{ booking.client.company | nullable }}
          </p>
        </div>
      </div>
      <div v-if="booking.client.department" class="form-item__field">
        <div class="input-group input-group-booking-detail">
          <span class="input-group-addon">{{ $t('department') | sentenceCase }}</span>
          <p id="company" @copy="handleCopy" class="form-control">
            {{ booking.client.department | nullable }}
          </p>
        </div>
      </div>
      <div v-if="userCanSeeClientBasicDetails && userCanSeeClientPii" class="form-item__field">
        <div class="input-group input-group-booking-detail">
          <span class="input-group-addon">Email</span>
          <p id="email" class="form-control" @copy="handleCopy"><a :href="`mailto:${booking.client.email}`">{{ booking.client.email }}</a></p>
        </div>
      </div>
      <div v-if="userCanSeeClientBasicDetails && userCanSeeClientPii" class="form-item__field">
        <div class="input-group input-group-booking-detail">
          <span class="input-group-addon">Phone</span>
          <p id="phone" class="form-control" @copy="handleCopy">{{ booking.client.phone | phoneNumber(booking.region ? booking.region.shortname : null) | nullable }}</p>
        </div>
      </div>
      <div class="form-item__field form-item__field--row" v-if="userCanSeeClientDiscoveryDetails && booking.client.found_pickstar">
        <div class="input-group input-group-booking-detail">
          <span class="input-group-addon">Found {{ clientName }}</span>
          <p class="form-control">
            {{ foundPickstarOptions.findIndex(element => element.id === booking.client.found_pickstar) !== -1
              ? foundPickstarOptions.find(element => element.id === booking.client.found_pickstar).text
              : booking.client.found_pickstar }}
            {{ booking.client.other_found_pickstar ? ' - ' + booking.client.other_found_pickstar : '' }}</p>
        </div>
      </div>
      <div v-if="!userCanSeeClientPii" class="form-item__row">
        <div v-if="this.bookingTypeIsBooking && this.findClientThread()">
          <p>
            To speak to the client about this booking, use our <a href="#drawer-messages-chat" :data-toggle="$chatsModuleEnabled ? 'drawer' : null" aria-foldedopen="false" aria-controls="drawer-messages-chat" @click="handleGetThreadCallback"> chat feature </a>. Alternatively, contact Pickstar.
          </p>
        </div>
        <div v-else>
          <p>
            Once the client has paid, speak to them directly via our chat. If you have any issues contact Pickstar.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { helpers } from 'mixins/bookings'
import { OpportunityTracker } from 'mixins/analytics'
import {cloneDeep, filter} from 'lodash'
import {mapActions} from 'vuex'

export default {
  mixins: [helpers, OpportunityTracker],

  props: {
    booking: {
      type: Object,
      required: true
    }
  },

  methods: {
    ...mapActions('message', ['getThread']),
    ...mapActions('booking', ['bookingThreadUnreadCount']),

    handleCopy (copyEvent) {
      this.trackEvent(this.analytics.V2.JobContactCopyEvent, { contactInfoType: copyEvent.target.id })
    },

    userCanSeeClientDiscoveryDetails () {
      return this.$userIsAdmin || this.$userIsClient
    },

    findClientThread () {
      // find a thread with current user and client
      let threadId = 0
      this.booking.threads.forEach((thread, index) => {
        let threadParticipant = filter(cloneDeep(thread.participants), current => (current.id === this.$user.id))
        let client = filter(cloneDeep(thread.participants), current => (current.role === 'client'))
        if ((threadParticipant.length === 1) && (client.length === 1)) {
          threadId = thread.id
        }
      })

      return threadId
    },

    handleGetThreadCallback () {
      let threadId = this.findClientThread()
      if (this.$chatsModuleEnabled && threadId) {
        this.trackEvent(this.analytics.V2.JobChatOpenEvent)
        const targetElement = document.getElementById('mainbody')
        targetElement.style.overflow = 'hidden'
        return this.getThread({ threadId, userId: this.$user.id, tenantSlug: this.client_config.slug })
          .then(() => {
            this.bookingThreadUnreadCount({ threadId, count: 0 })
          })
      }
    }
  },

  data: function () {
    return {
      foundPickstarOptions: [
        { id: 'google', text: 'Google' },
        { id: 'facebook_ads', text: 'Facebook Ads' },
        { id: 'previous_client', text: 'Previous Client' },
        { id: 'linkedin', text: 'LinkedIn' },
        { id: 'instagram', text: 'Instagram' },
        { id: 'influencer', text: 'Athlete Influencer' },
        { id: 'newsletter', text: 'Newsletter' },
        { id: 'television', text: 'Television' },
        { id: 'referral', text: 'Referral' },
        { id: 'outbound', text: 'Email' },
        { id: 'other', text: 'Other' }
      ]
    }
  },

  computed: {
    userCanSeeClientBasicDetails () {
      if (this.$userIsAdmin || this.$userIsClient || !!this.$showClientPii) {
        return true
      }

      if (this.bookingTypeIsBookingRequestOrDraft) {
        return false
      } else if (this.bookingTypeIsBooking) {
        if (this.$userIsTalent && this.bookingCurrentTalent.response.client_accepted === false) {
          return false
        }

        if (this.$userIsManager && this.booking.talent.every(talent => talent.response.client_accepted === false)) {
          return false
        }
        return true
      }

      return false
    },

    userCanSeeClientPii () {
      if (this.$userIsAdmin || this.$userIsClient) {
        return true
      }

      return !!this.$showClientPii
    }
  }
}
</script>
